import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { Mail, Phone, LogIn, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import logo from "../assets/logo64.png"; // Using the smaller logo for consistency

const Login: React.FC = () => {
  const [method, setMethod] = useState<"email" | "phone">("email");
  const [email, setEmail] = useState(()=> sessionStorage.getItem("email") || "");
  const [phone, setPhone] = useState(()=> sessionStorage.getItem("phone") || "");
  const [name] = useState(()=> sessionStorage.getItem("name") || "");
  const [referalcode] = useState(()=> sessionStorage.getItem("referalCode") || "");
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const { signInWithOTP, signInWithPhoneOTP, verifyOTP, profile } = useAuth();
  const navigate = useNavigate();
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (profile) {
      if (profile.admin === "Y") {
        navigate("/adminhome");
      } else {
        navigate("/home");
      }
    }
  }, [profile, navigate]);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (method === "email") {
        await signInWithOTP(email);
        toast.success("OTP sent to your email!");
      } else {
        await signInWithPhoneOTP(phone);
        toast.success("OTP sent to your phone!");
      }
      setOtpSent(true);
      setTimeout(() => otpRefs.current[0]?.focus(), 120);
    } catch (err: any) {
      toast.error(err?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const code = otp.join("");
      await verifyOTP(method === "email" ? email : phone, code);
      toast.success("Identity Verified");
    } catch (err: any) {
      toast.error("Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < otp.length - 1) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
    if (e.key === "ArrowLeft" && index > 0) otpRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < otp.length - 1) otpRefs.current[index + 1]?.focus();
  };

  return (
    <div className="min-h-screen bg-[#08090a] text-white antialiased font-sans selection:bg-orange-500/30">
      {/* --- BACKGROUND DECOR --- */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,#1a1b23_0%,transparent_50%)] pointer-events-none" />
      <div className="fixed top-[-10%] right-[-10%] w-[40%] h-[40%] bg-orange-500/5 rounded-full blur-[120px] pointer-events-none" />

      {/* --- NAVBAR --- */}
      <nav className="sticky top-0 z-50 backdrop-blur-2xl bg-black/40 border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group cursor-pointer no-underline">
            <div className="relative w-9 h-9">
              <div className="absolute inset-0 bg-orange-500 blur-md opacity-40 group-hover:opacity-100 transition-opacity"></div>
              <img src={logo} alt="V" className="relative w-full h-full object-cover rounded-lg border border-white/20" />
            </div>
            <span className="text-2xl font-black tracking-tighter italic bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">ytta</span>
          </Link>
          
          <Link to="/register" className="text-xs font-black tracking-widest uppercase text-gray-500 hover:text-white transition-colors">
            Join Now
          </Link>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-6 pt-16 pb-24 relative z-10 flex flex-col items-center">
        
        {/* --- HEADER --- */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-500 text-[10px] font-black tracking-widest uppercase mb-4">
             <ShieldCheck size={12} /> Secure Gateway
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4 italic">
            Welcome <span className="bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">Back</span>
          </h1>
          <p className="text-gray-500 text-sm max-w-xs mx-auto leading-relaxed">
            Access your institutional signal dashboard and market analytics.
          </p>
        </div>

        {/* --- LOGIN CARD --- */}
        <div className="w-full relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-orange-500/20 to-orange-500/0 rounded-[2.5rem] blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
          
          <div className="relative bg-white/[0.02] backdrop-blur-xl rounded-[2.5rem] p-8 md:p-12 border border-white/5 flex flex-col items-center">
            
            {!otpSent ? (
              <form onSubmit={handleSendOTP} className="w-full space-y-8">
                {/* Method Toggle */}
                <div className="flex p-1 bg-white/5 rounded-2xl border border-white/5">
                  <button
                    type="button"
                    onClick={() => setMethod("email")}
                    className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 text-xs font-black tracking-widest uppercase transition-all ${
                      method === "email" ? "bg-white text-black shadow-lg" : "text-gray-500 hover:text-white"
                    }`}
                  >
                    <Mail size={14} /> Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod("phone")}
                    className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 text-xs font-black tracking-widest uppercase transition-all ${
                      method === "phone" ? "bg-white text-black shadow-lg" : "text-gray-500 hover:text-white"
                    }`}
                  >
                    <Phone size={14} /> Phone
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="relative">
                    <input
                      type={method === "email" ? "email" : "tel"}
                      value={method === "email" ? email : phone}
                      onChange={(e) => method === "email" ? setEmail(e.target.value) : setPhone(e.target.value)}
                      placeholder={method === "email" ? "institutional@email.com" : "+91 00000 00000"}
                      className="w-full px-6 py-5 bg-white/[0.03] rounded-2xl border border-white/5 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50 outline-none transition-all text-center font-bold tracking-tight text-lg"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-5 bg-white text-black hover:bg-orange-500 hover:text-white rounded-2xl font-black text-xs tracking-[0.3em] uppercase transition-all duration-500 disabled:opacity-20 flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Request OTP <ArrowRight size={16} /></>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="w-full space-y-8 flex flex-col items-center">
                <div className="text-center">
                  <h3 className="text-lg font-black italic uppercase tracking-tighter text-white">Verification Code</h3>
                  <p className="text-xs text-gray-500 mt-2">
                    Sent to <span className="text-orange-500">{method === "email" ? email : phone}</span>
                  </p>
                </div>

                <div className="flex gap-2 justify-center">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(e.target.value.trim(), i)}
                      onKeyDown={(e) => handleOtpKeyDown(e, i)}
                      className="w-12 h-16 text-center text-2xl font-black rounded-xl bg-white/5 border border-white/10 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-white transition-all"
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-5 bg-orange-500 text-white rounded-2xl font-black text-xs tracking-[0.3em] uppercase shadow-xl shadow-orange-500/20 hover:scale-[1.02] active:scale-95 transition-all"
                >
                  {loading ? "Authorizing..." : "Authenticate"}
                </button>

                <button
                  type="button"
                  onClick={() => setOtpSent(false)}
                  className="text-[10px] font-black text-gray-600 hover:text-white uppercase tracking-[0.2em] transition-colors"
                >
                  ← Change {method}
                </button>
              </form>
            )}
          </div>
        </div>

        <p className="mt-12 text-[10px] font-black text-gray-700 uppercase tracking-[0.3em] flex items-center gap-2">
          <Sparkles size={12} className="text-orange-500" /> Secure Terminal Encryption
        </p>
      </div>
    </div>
  );
};

export default Login;